/**
 * Formatting helpers. Every timestamp on the wire is epoch ms UTC; everything
 * here renders it in the admin's local zone, which is the only place a local
 * time may appear.
 */

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const DATE_ONLY = new Intl.DateTimeFormat(undefined, {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const TIME_ONLY = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

export function formatDateTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  return DATE_TIME.format(new Date(ms));
}

export function formatDate(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  return DATE_ONLY.format(new Date(ms));
}

export function formatTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  return TIME_ONLY.format(new Date(ms));
}

/** `mm:ss`, or `h:mm:ss` past an hour — the timeline and player clock. */
export function formatClock(ms: number): string {
  const safe = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/**
 * A position on the attempt timeline, which legitimately starts *before* t=0.
 *
 * t=0 is `attempts.started_at`, but the phone records from pairing onward —
 * placement, paper check and room scan all happen before the exam begins — so
 * its early chunks carry negative offsets. Those read as `−1:01`, so a glance
 * at a timestamp says whether it is setup footage or exam footage.
 */
export function formatOffset(ms: number): string {
  return ms < -499 ? `−${formatClock(-ms)}` : formatClock(ms);
}

/** Compact duration for tables: `4m 12s`, `1h 06m`. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

/** Relative day label for upcoming exams — "in 3 days", "today", "9 days ago". */
export function formatRelativeDays(ms: number | null, now = Date.now()): string {
  if (ms === null) return "Not scheduled";
  const days = Math.round((ms - now) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";
  return days > 0 ? `In ${days} days` : `${Math.abs(days)} days ago`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Scores print at most two decimals but never carry trailing zeros. */
export function formatMarks(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

export function formatPercent(ratio: number, digits = 0): string {
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** `datetime-local` input value from epoch ms, in the admin's own zone. */
export function toDateTimeLocal(ms: number | null): string {
  if (ms === null) return "";
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
}

/** Inverse of `toDateTimeLocal`. */
export function fromDateTimeLocal(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}
